/*
 * Shop overview page (sections shop-overview-*).
 *
 * Three custom elements, each independent of the others:
 *
 *   shop-overview-card     one product: colour choice and add to cart
 *   shop-overview-systems  the mobile tab bar driving the system slider
 *   shop-overview-filter   category filter and sort over the accessory grid
 *
 * Adding never reloads the page. The cart notification and the icon bubble are
 * re-rendered from the sections the add response carries, the same way Dawn's
 * own product form does it, so the page behaves like every other add in the
 * theme.
 *
 * Every section of the family pulls this file in, because any one of them can
 * be used on its own. Shopify does not deduplicate those script tags, so the
 * browser runs the file once per section on the page — hence the block below:
 * the first run defines the elements, the rest return before redeclaring them.
 */

if (!customElements.get('shop-overview-card')) {
  /*
   * One product card.
   *
   * The quantity already in the cart is rendered into a JSON block as a
   * variant id -> quantity map, so the button shows the real state on a reload
   * and stays right when the colour changes. From then on it is kept in step
   * through the cartUpdate event rather than locally: the same product can sit
   * in the systems row and in the accessory grid at once, and both cards have
   * to follow an add made in either of them.
   */
  class ShopOverviewCard extends HTMLElement {
    connectedCallback() {
      if (this.initialised) return;
      this.initialised = true;

      this.button = this.querySelector('[data-shop-ov-add]');
      if (!this.button) return;

      this.priceElement = this.querySelector('[data-shop-ov-price]');
      this.imageElement = this.querySelector('[data-shop-ov-image]');
      this.errorElement = this.querySelector('[data-shop-ov-error]');

      const variantData = this.querySelector('[data-shop-ov-variants]');
      this.variants = variantData ? JSON.parse(variantData.textContent) : [];

      const inCartData = this.querySelector('[data-shop-ov-in-cart]');
      this.inCart = inCartData ? JSON.parse(inCartData.textContent) : {};

      this.addEventListener('change', this.onOptionChange.bind(this));
      this.button.addEventListener('click', this.onAdd.bind(this));

      this.cartUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.cartUpdate, this.onCartUpdate.bind(this));

      this.refresh();
    }

    disconnectedCallback() {
      if (this.cartUpdateUnsubscriber) this.cartUpdateUnsubscriber();
      this.cartUpdateUnsubscriber = undefined;

      /* Let connectedCallback run again. Re-rendering a section moves its
         cards rather than rebuilding them, and a card that came back with the
         guard still set would have no cartUpdate subscription left. */
      this.initialised = false;
    }

    onOptionChange(event) {
      const input = event.target;
      if (!input.matches('[data-shop-ov-option]')) return;

      const variant = this.variants.find((entry) => entry.id === Number(input.value));
      if (!variant) return;

      this.dataset.variantId = variant.id;
      this.setError('');

      if (this.priceElement) this.priceElement.textContent = variant.price;
      if (this.imageElement && variant.image) this.imageElement.src = variant.image;

      this.refresh();
    }

    onCartUpdate(event) {
      if (!event || !event.variantId) return;
      if (!this.holds(event.variantId)) return;

      /* Read the quantity back out of the cart instead of counting adds by
         one: Shopify caps a line at the stock on hand, so an add of one does
         not always move the line by one, and a line emptied from the line list
         has to take the counter back to zero. Both payloads that carry a
         variantId bring their items along — the add response the lines it
         touched, cart.js the whole cart — so a variant missing from the list
         is only ever one this update did not leave in the cart. */
      const items = event.cartData && event.cartData.items;

      if (Array.isArray(items)) {
        const line = items.find((item) => Number(item.variant_id) === Number(event.variantId));
        this.inCart[event.variantId] = line ? line.quantity : 0;
      } else {
        this.inCart[event.variantId] = (this.inCart[event.variantId] || 0) + 1;
      }

      this.refresh();
    }

    /* A card holds a variant when it can add it — its current one, or one of
       the colours it offers. */
    holds(variantId) {
      if (Number(this.dataset.variantId) === Number(variantId)) return true;
      return this.variants.some((variant) => Number(variant.id) === Number(variantId));
    }

    refresh() {
      const quantity = this.inCart[this.dataset.variantId] || 0;
      const label = this.button.querySelector('[data-shop-ov-add-label]') || this.button;

      if (quantity > 0) {
        /* The placeholder is spelled %count% rather than Liquid's own
           {{ count }}: the label travels through an attribute, and the first
           `}}` inside an output tag would close it early. */
        const template = this.button.dataset.labelInCart || '';
        label.textContent = template.replace('%count%', quantity);
      } else if (!this.button.disabled) {
        /* Only a button that can actually add gets the add label. A sold out
           one keeps the wording Liquid rendered into it, which this line would
           otherwise overwrite on the very first refresh. */
        label.textContent = this.button.dataset.labelAdd || label.textContent;
      }

      this.classList.toggle('is-added', quantity > 0);
      this.button.classList.toggle('is-added', quantity > 0);
    }

    onAdd() {
      const variantId = this.dataset.variantId;
      if (!variantId || this.button.getAttribute('aria-busy') === 'true') return;

      this.button.setAttribute('aria-busy', 'true');
      this.setError('');

      const cart = document.querySelector('cart-drawer') || document.querySelector('cart-notification');
      const sections = cart ? cart.getSectionsToRender().map((section) => section.id) : ['cart-icon-bubble'];

      const body = JSON.stringify({
        items: [{ id: Number(variantId), quantity: 1 }],
        sections: sections,
        sections_url: window.location.pathname,
      });

      fetch(`${routes.cart_add_url}`, { ...fetchConfig(), ...{ body } })
        .then((response) => response.json())
        .then((response) => {
          if (response.status) {
            this.setError(response.description || window.cartStrings.error);
            publish(PUB_SUB_EVENTS.cartError, {
              source: 'shop-overview',
              productVariantId: variantId,
              errors: response.errors || response.description,
              message: response.message,
            });
            return;
          }

          /* Dawn's cart notification picks the line it shows by `key` on the
             top level of the response. Only a request shaped like its own
             product form gets one there; an items array puts the key on each
             item instead. Whatever goes wrong from here must not read as "the
             add failed" — by this point the item is in the cart. */
          try {
            if (cart) {
              if (!response.key && response.items && response.items.length > 0) {
                response.key = response.items[0].key;
              }
              cart.renderContents(response);
            } else {
              this.renderCartIcon(response.sections);
            }
          } catch (error) {
            console.error(error);
          }

          publish(PUB_SUB_EVENTS.cartUpdate, {
            source: 'shop-overview',
            productVariantId: variantId,
            variantId: Number(variantId),
            cartData: response,
          });
        })
        .catch((error) => {
          console.error(error);
          this.setError(window.cartStrings.error);
        })
        .finally(() => {
          this.button.removeAttribute('aria-busy');
        });
    }

    /* Only reached when the theme renders neither drawer nor notification */
    renderCartIcon(sections) {
      const container = document.getElementById('cart-icon-bubble');
      const markup = sections && sections['cart-icon-bubble'];
      if (!container || !markup) return;

      const parsed = new DOMParser().parseFromString(markup, 'text/html').querySelector('.shopify-section');
      if (parsed) container.innerHTML = parsed.innerHTML;
    }

    setError(message) {
      if (this.errorElement) this.errorElement.textContent = message;
    }
  }

  /*
   * Tab bar over the system slider, mobile only.
   *
   * Above the breakpoint the cards are a plain grid and the tab bar is hidden,
   * so the listeners stay idle rather than being torn down: matchMedia decides
   * on every interaction, which also survives a rotation without rebinding.
   */
  class ShopOverviewSystems extends HTMLElement {
    connectedCallback() {
      if (this.initialised) return;
      this.initialised = true;

      this.rail = this.querySelector('[data-shop-ov-rail]');
      this.tabs = Array.from(this.querySelectorAll('[data-shop-ov-tab]'));
      this.cards = Array.from(this.querySelectorAll('[data-shop-ov-system]'));
      if (!this.rail || this.tabs.length === 0) return;

      this.mobile = window.matchMedia('(max-width: 749px)');

      this.tabs.forEach((tab) => {
        tab.addEventListener('click', () => this.goTo(Number(tab.dataset.shopOvTab)));
      });

      this.rail.addEventListener('scroll', this.onScroll.bind(this), { passive: true });
    }

    goTo(index) {
      const card = this.cards[index];
      if (!card) return;

      this.select(index);

      if (!this.mobile.matches) {
        card.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
        return;
      }

      this.rail.scrollTo({
        left: card.offsetLeft - (this.rail.clientWidth - card.clientWidth) / 2,
        behavior: 'smooth',
      });
    }

    onScroll() {
      if (!this.mobile.matches) return;

      /* One frame is enough to keep a fling from re-measuring on every tick */
      if (this.scrollFrame) return;
      this.scrollFrame = window.requestAnimationFrame(() => {
        this.scrollFrame = null;

        const middle = this.rail.scrollLeft + this.rail.clientWidth / 2;
        let nearest = 0;
        let shortest = Infinity;

        this.cards.forEach((card, index) => {
          const distance = Math.abs(card.offsetLeft + card.clientWidth / 2 - middle);
          if (distance < shortest) {
            shortest = distance;
            nearest = index;
          }
        });

        this.select(nearest);
      });
    }

    select(index) {
      this.tabs.forEach((tab) => {
        tab.setAttribute('aria-pressed', Number(tab.dataset.shopOvTab) === index ? 'true' : 'false');
      });
    }
  }

  /*
   * Category filter and sort over the accessory grid.
   *
   * Everything the grid needs is already in the markup, so filtering stays an
   * attribute change instead of a round trip: sorting reorders through the CSS
   * `order` property, which leaves the DOM — and therefore focus and the screen
   * reader's position — untouched.
   */
  class ShopOverviewFilter extends HTMLElement {
    connectedCallback() {
      if (this.initialised) return;
      this.initialised = true;

      this.filters = Array.from(this.querySelectorAll('[data-shop-ov-filter]'));
      this.sort = this.querySelector('[data-shop-ov-sort]');
      this.items = Array.from(this.querySelectorAll('[data-shop-ov-item]'));
      this.empty = this.querySelector('[data-shop-ov-empty]');

      this.category = '';

      this.filters.forEach((filter) => {
        filter.addEventListener('click', () => {
          this.category = filter.dataset.shopOvFilter;
          this.apply();
        });
      });

      if (this.sort) this.sort.addEventListener('change', this.apply.bind(this));

      this.apply();
    }

    apply() {
      this.filters.forEach((filter) => {
        filter.setAttribute('aria-pressed', filter.dataset.shopOvFilter === this.category ? 'true' : 'false');
      });

      let visible = this.items.filter((item) => this.category === '' || item.dataset.category === this.category);

      const mode = this.sort ? this.sort.value : 'recommended';
      if (mode === 'price-ascending') {
        visible = visible.slice().sort((a, b) => Number(a.dataset.price) - Number(b.dataset.price));
      } else if (mode === 'price-descending') {
        visible = visible.slice().sort((a, b) => Number(b.dataset.price) - Number(a.dataset.price));
      }

      this.items.forEach((item) => {
        item.hidden = !visible.includes(item);
        item.style.order = '';
      });

      visible.forEach((item, index) => {
        item.style.order = index;
      });

      if (this.empty) this.empty.hidden = visible.length > 0;
    }
  }

  customElements.define('shop-overview-card', ShopOverviewCard);
  customElements.define('shop-overview-systems', ShopOverviewSystems);
  customElements.define('shop-overview-filter', ShopOverviewFilter);
}
